import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, MapPin, Phone, Award, Clock, Languages, 
  IndianRupee, CheckCircle, ArrowLeft, ArrowRight, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry"
];

const LANGUAGES = [
  "Hindi", "Sanskrit", "English", "Tamil", "Telugu", "Kannada", "Malayalam",
  "Marathi", "Gujarati", "Bengali", "Punjabi", "Odia", "Assamese"
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function PanditRegistrationPage({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingProfile, setExistingProfile] = useState(null);
  const [poojaTypes, setPoojaTypes] = useState([]);
  
  const [formData, setFormData] = useState({
    // Step 1: Location
    city: '',
    state: '',
    pincode: '',
    address: '',
    // Step 2: Services
    puja_types: [],
    specialization: '',
    // Step 3: Experience
    experience_years: '',
    qualification: '',
    gurukul_name: '',
    languages: [],
    // Step 4: Fees & Contact
    fees_per_puja: '',
    fees_range_min: '',
    fees_range_max: '',
    phone: '',
    whatsapp: '',
    available_days: DAYS
  });

  useEffect(() => {
    if (!user) {
      toast.error('Please login to register as Pandit');
      navigate('/auth');
      return;
    }
    fetchPoojaTypes();
    checkExistingProfile();
  }, [user, navigate]);

  const fetchPoojaTypes = async () => {
    try {
      const res = await axios.get(`${API}/pandit/pooja-types`);
      setPoojaTypes(res.data);
    } catch (error) {
      console.error('Failed to load pooja types');
    }
  };

  const checkExistingProfile = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/pandit/my-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setExistingProfile(res.data);
        // Pre-fill form with existing data
        setFormData({
          city: res.data.city || '',
          state: res.data.state || '',
          pincode: res.data.pincode || '',
          address: res.data.address || '',
          puja_types: res.data.puja_types || [],
          specialization: res.data.specialization || '',
          experience_years: res.data.experience_years?.toString() || '',
          qualification: res.data.qualification || '',
          gurukul_name: res.data.gurukul_name || '',
          languages: res.data.languages || [],
          fees_per_puja: res.data.fees_per_puja?.toString() || '',
          fees_range_min: res.data.fees_range_min?.toString() || '',
          fees_range_max: res.data.fees_range_max?.toString() || '',
          phone: res.data.phone || '',
          whatsapp: res.data.whatsapp || '',
          available_days: res.data.available_days || DAYS
        });
      }
    } catch (error) {
      // No existing profile
    }
  };

  const handlePoojaTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      puja_types: prev.puja_types.includes(type)
        ? prev.puja_types.filter(t => t !== type)
        : [...prev.puja_types, type]
    }));
  };

  const handleLanguageToggle = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day]
    }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        if (!formData.city || !formData.state) {
          toast.error('Please fill city and state');
          return false;
        }
        return true;
      case 2:
        if (formData.puja_types.length === 0) {
          toast.error('Please select at least one pooja type');
          return false;
        }
        return true;
      case 3:
        if (!formData.experience_years || formData.languages.length === 0) {
          toast.error('Please fill experience and select at least one language');
          return false;
        }
        return true;
      case 4:
        if (!formData.fees_per_puja || !formData.phone) {
          toast.error('Please fill fees and phone number');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const payload = {
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode || null,
        address: formData.address || null,
        puja_types: formData.puja_types,
        specialization: formData.specialization || null,
        experience_years: parseInt(formData.experience_years),
        qualification: formData.qualification || null,
        gurukul_name: formData.gurukul_name || null,
        languages: formData.languages,
        fees_per_puja: parseFloat(formData.fees_per_puja),
        fees_range_min: parseFloat(formData.fees_range_min) || parseFloat(formData.fees_per_puja),
        fees_range_max: parseFloat(formData.fees_range_max) || parseFloat(formData.fees_per_puja) * 3,
        phone: formData.phone,
        whatsapp: formData.whatsapp || null,
        available_days: formData.available_days
      };

      if (existingProfile) {
        await axios.put(`${API}/pandit/profile`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Profile updated successfully!');
      } else {
        await axios.post(`${API}/pandit/profile`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Registration successful! Welcome, Pandit Ji!');
      }
      
      navigate('/pandit/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Location', icon: MapPin },
    { num: 2, title: 'Services', icon: Award },
    { num: 3, title: 'Experience', icon: Clock },
    { num: 4, title: 'Fees', icon: IndianRupee }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF9933] to-[#FF6600] text-white px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            {existingProfile ? 'Update Your Profile' : 'Register as Pandit Ji'}
          </h1>
          <p className="text-orange-100 mt-1">
            {existingProfile ? 'Keep your profile updated for better visibility' : 'Join our platform to connect with devotees'}
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-2xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-2xl shadow-lg p-4">
          <div className="flex items-center justify-between">
            {steps.map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex flex-col items-center ${i > 0 ? 'ml-2' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    step >= s.num 
                      ? 'bg-[#FF9933] text-white' 
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    {step > s.num ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <s.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 hidden md:block ${
                    step >= s.num ? 'text-[#FF9933] font-medium' : 'text-gray-400'
                  }`}>
                    {s.title}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-8 md:w-16 h-1 mx-1 md:mx-2 rounded ${
                    step > s.num ? 'bg-[#FF9933]' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto px-4 mt-6">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {/* Step 1: Location */}
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Where are you located?</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Varanasi"
                    className="rounded-xl mt-1"
                    data-testid="reg-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(v) => setFormData({ ...formData, state: v })}
                  >
                    <SelectTrigger className="rounded-xl mt-1" data-testid="reg-state-select">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="pincode">Pincode</Label>
                <Input
                  id="pincode"
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  placeholder="e.g., 221001"
                  className="rounded-xl mt-1"
                  data-testid="reg-pincode-input"
                />
              </div>

              <div>
                <Label htmlFor="address">Full Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address, landmark, etc."
                  rows={2}
                  className="rounded-xl mt-1"
                  data-testid="reg-address-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Services */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">What poojas do you perform?</h2>
              
              <div>
                <Label>Select Pooja Types *</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {poojaTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handlePoojaTypeToggle(type)}
                      className={`px-3 py-2 text-sm rounded-xl border transition-colors text-left ${
                        formData.puja_types.includes(type)
                          ? 'bg-orange-50 border-[#FF9933] text-[#FF9933]'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300'
                      }`}
                      data-testid={`pooja-type-${type.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {formData.puja_types.includes(type) && (
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                      )}
                      {type}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {formData.puja_types.length} pooja types
                </p>
              </div>

              <div>
                <Label htmlFor="specialization">Specialization (Optional)</Label>
                <Textarea
                  id="specialization"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  placeholder="Describe your areas of expertise..."
                  rows={3}
                  className="rounded-xl mt-1"
                  data-testid="reg-specialization-input"
                />
              </div>
            </div>
          )}

          {/* Step 3: Experience */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Tell us about your experience</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="experience_years">Years of Experience *</Label>
                  <Input
                    id="experience_years"
                    type="number"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                    placeholder="e.g., 10"
                    className="rounded-xl mt-1"
                    data-testid="reg-experience-input"
                  />
                </div>
                <div>
                  <Label htmlFor="qualification">Qualification</Label>
                  <Input
                    id="qualification"
                    value={formData.qualification}
                    onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                    placeholder="e.g., Acharya, Shastri"
                    className="rounded-xl mt-1"
                    data-testid="reg-qualification-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="gurukul_name">Gurukul/Institution Name</Label>
                <Input
                  id="gurukul_name"
                  value={formData.gurukul_name}
                  onChange={(e) => setFormData({ ...formData, gurukul_name: e.target.value })}
                  placeholder="Where did you study?"
                  className="rounded-xl mt-1"
                  data-testid="reg-gurukul-input"
                />
              </div>

              <div>
                <Label>Languages *</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => handleLanguageToggle(lang)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        formData.languages.includes(lang)
                          ? 'bg-orange-50 border-[#FF9933] text-[#FF9933]'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300'
                      }`}
                      data-testid={`lang-${lang.toLowerCase()}`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Available Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DAYS.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                        formData.available_days.includes(day)
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'bg-white border-gray-200 text-gray-400'
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Fees & Contact */}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Set your fees & contact</h2>
              
              <div>
                <Label htmlFor="fees_per_puja">Base Fee per Pooja (₹) *</Label>
                <Input
                  id="fees_per_puja"
                  type="number"
                  value={formData.fees_per_puja}
                  onChange={(e) => setFormData({ ...formData, fees_per_puja: e.target.value })}
                  placeholder="e.g., 1100"
                  className="rounded-xl mt-1"
                  data-testid="reg-fee-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fees_range_min">Minimum Fee (₹)</Label>
                  <Input
                    id="fees_range_min"
                    type="number"
                    value={formData.fees_range_min}
                    onChange={(e) => setFormData({ ...formData, fees_range_min: e.target.value })}
                    placeholder="e.g., 500"
                    className="rounded-xl mt-1"
                    data-testid="reg-fee-min-input"
                  />
                </div>
                <div>
                  <Label htmlFor="fees_range_max">Maximum Fee (₹)</Label>
                  <Input
                    id="fees_range_max"
                    type="number"
                    value={formData.fees_range_max}
                    onChange={(e) => setFormData({ ...formData, fees_range_max: e.target.value })}
                    placeholder="e.g., 5100"
                    className="rounded-xl mt-1"
                    data-testid="reg-fee-max-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="e.g., 9876543210"
                    className="rounded-xl mt-1"
                    data-testid="reg-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="whatsapp">WhatsApp Number</Label>
                  <Input
                    id="whatsapp"
                    type="tel"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    placeholder="Same as phone?"
                    className="rounded-xl mt-1"
                    data-testid="reg-whatsapp-input"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-orange-50 rounded-xl p-4 mt-6">
                <h3 className="font-semibold text-gray-900 mb-2">Profile Summary</h3>
                <div className="text-sm text-gray-700 space-y-1">
                  <p><strong>Location:</strong> {formData.city}, {formData.state}</p>
                  <p><strong>Services:</strong> {formData.puja_types.length} pooja types</p>
                  <p><strong>Experience:</strong> {formData.experience_years} years</p>
                  <p><strong>Fees:</strong> ₹{formData.fees_range_min || formData.fees_per_puja} - ₹{formData.fees_range_max || (parseFloat(formData.fees_per_puja) * 3)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-4 border-t">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={prevStep}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            ) : (
              <div />
            )}

            {step < 4 ? (
              <Button
                onClick={nextStep}
                className="rounded-xl bg-[#FF9933] hover:bg-[#E68A00]"
                data-testid="next-step-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="rounded-xl bg-[#FF9933] hover:bg-[#E68A00] px-8"
                data-testid="submit-registration-btn"
              >
                {loading ? 'Saving...' : (existingProfile ? 'Update Profile' : 'Complete Registration')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
